import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useSearchParams,
} from "react-router-dom";
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import WindowServiceRequestForm from "../../../Forms/WindowDoor/WindowServiceRequestForm";
import { selectUser } from "../../../../features/userSlice";
import DataTable from "../../../../components/WindowDoor/ServiceRequests/DataTable";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Button } from "@mui/material";
import ServiceRequestReports, {
  exportRequestsCsv,
} from "./ServiceRequestReports";
import {
  STATUS_RAIL,
  addressLabel,
  agingLevel,
  customerLabel,
  daysActive,
  isOpen,
  shortDate,
  statusStyle,
} from "../../../../components/WindowDoor/ServiceRequests/constants";
import {
  assignServiceRequest,
  fetchServiceRequestStats,
  fetchServiceRequests,
  fetchServiceTechnicians,
} from "../../../../components/WindowDoor/ServiceRequests/api";
import {
  ActivityLogButton,
  StatusChip,
  rememberDashboardScroll,
  rememberDashboardSearch,
  takeDashboardScroll,
} from "./ServiceRequestShared";
import HomeRepairServiceOutlinedIcon from "@mui/icons-material/HomeRepairServiceOutlined";
import { PageHeader, PageShell } from "../../../../components/UI/Page";
import { Segmented } from "../../../../components/WindowDoor/wdUi";

const AGING_HINT = {
  critical: "Open 14+ days",
  warning: "Open 7+ days",
};

// Imported assignments are name-only (the techs predate their Big C Node
// accounts), so an assignee is identified by userID when there is one and by
// name otherwise — everywhere: the filter, the workload panel, the dropdown.
const assigneeKey = (assignedTo) =>
  assignedTo?.userID != null && assignedTo.userID !== ""
    ? String(assignedTo.userID)
    : assignedTo?.name || "";

// The whole view lives in the URL so Back and refresh land on it again:
//   view=reports  status  assignee  q  sort=<column>.<asc|desc>
//   page (1-based)  size
// Defaults are left out, so the plain path is the default view.
const DEFAULT_SORT = { key: "entry", dir: "desc" };
const DEFAULT_PAGE_SIZE = 25;

const tableStateFromParams = (params) => {
  const [key, dir] = (params.get("sort") || "").split(".");
  return {
    query: params.get("q") || "",
    sort: key && (dir === "asc" || dir === "desc") ? { key, dir } : DEFAULT_SORT,
    page: Math.max(0, (Number(params.get("page")) || 1) - 1),
    pageSize: Number(params.get("size")) || DEFAULT_PAGE_SIZE,
  };
};

const ServiceRequestsDashboard = () => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("view") === "reports" ? "reports" : "queue";
  const statusFilter = searchParams.get("status") || "open";
  const assigneeFilter = searchParams.get("assignee") || "all";
  const [newRequestOpen, setNewRequestOpen] = useState(false);

  const [requests, setRequests] = useState(null);
  const [stats, setStats] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);

  // Filter and table changes replace the history entry rather than adding
  // one, so Back still leaves the page instead of undoing a filter.
  // null/"" drops a param.
  const updateParams = (patch, { push = false } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") next.delete(key);
      else next.set(key, String(value));
    });
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: !push });
    }
  };

  const setStatusFilter = (value) =>
    updateParams({ status: value === "open" ? null : value });
  const setAssigneeFilter = (value) =>
    updateParams({ assignee: value === "all" ? null : value });

  const onTableChange = ({ query, sort, page, pageSize }) =>
    updateParams({
      q: query,
      sort:
        sort && (sort.key !== DEFAULT_SORT.key || sort.dir !== DEFAULT_SORT.dir)
          ? `${sort.key}.${sort.dir}`
          : null,
      page: page > 0 ? page + 1 : null,
      size: pageSize !== DEFAULT_PAGE_SIZE ? pageSize : null,
    });

  const search = searchParams.toString();
  useEffect(() => {
    rememberDashboardSearch(search);
  }, [search]);

  // Back, refresh, or the detail page's "All requests" link: return to where
  // the list was scrolled once the rows are in (they're refetched, so the
  // page starts short). Opening the page fresh from the menu starts at the top.
  const navigationType = useNavigationType();
  const location = useLocation();
  const restoreScroll =
    navigationType === "POP" || Boolean(location.state?.restoreScroll);
  const loaded = requests !== null;
  useEffect(() => {
    if (!loaded) return;
    const y = takeDashboardScroll(search);
    if (y && restoreScroll) {
      window.requestAnimationFrame(() => window.scrollTo(0, y));
    }
    // Once per visit, on first load — not on every filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  useEffect(() => {
    const save = () => rememberDashboardScroll(search);
    window.addEventListener("pagehide", save);
    return () => window.removeEventListener("pagehide", save);
  }, [search]);

  const openRequest = (id) => {
    rememberDashboardScroll(search);
    navigate(`/windowdoor/service-requests/${id}`);
  };

  const refresh = () => {
    fetchServiceRequests({ scope: "all", status: "all" })
      .then((data) => setRequests(Array.isArray(data) ? data : []))
      .catch((err) => {
        setRequests([]);
        setError(err.message || "Could not load service requests.");
      });
    fetchServiceRequestStats()
      .then(setStats)
      .catch(() => setStats(null));
  };

  useEffect(() => {
    refresh();
    fetchServiceTechnicians()
      .then((data) => setTechnicians(Array.isArray(data) ? data : []))
      .catch(() => setTechnicians([]));
  }, []);

  const visible = useMemo(() => {
    const all = requests || [];
    return all.filter((request) => {
      if (statusFilter === "open" && !isOpen(request.status)) return false;
      if (
        statusFilter !== "open" &&
        statusFilter !== "all" &&
        request.status !== statusFilter
      ) {
        return false;
      }
      const key = assigneeKey(request.assigned_to);
      if (assigneeFilter === "unassigned" && key) return false;
      if (
        assigneeFilter !== "all" &&
        assigneeFilter !== "unassigned" &&
        key !== String(assigneeFilter)
      ) {
        return false;
      }
      return true;
    });
  }, [requests, statusFilter, assigneeFilter]);

  // Filterable assignees: every real technician account, plus any name-only
  // assignee present in the data (imported history).
  const assigneeOptions = useMemo(() => {
    const options = new Map();
    technicians.forEach((technician) =>
      options.set(String(technician.userID), { key: String(technician.userID), name: technician.name })
    );
    (requests || []).forEach((request) => {
      const key = assigneeKey(request.assigned_to);
      if (key && !options.has(key)) {
        options.set(key, { key, name: request.assigned_to?.name || key });
      }
    });
    return [...options.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [technicians, requests]);

  const assign = async (request, userID) => {
    // Re-selecting an imported name-only assignee is a no-op, not an unassign.
    if (String(userID).startsWith("name:")) return;
    setSavingId(request._id);
    setError(null);
    try {
      const technician = technicians.find(
        (item) => String(item.userID) === String(userID)
      );
      await assignServiceRequest(
        request._id,
        technician
          ? {
              userID: technician.userID,
              name: technician.name,
              email: technician.email,
            }
          : null,
        { userID: user?.userID, name: user?.name, email: user?.email }
      );
      refresh();
    } catch (err) {
      setError(err.message || "Could not save the assignment.");
    } finally {
      setSavingId(null);
    }
  };

  const railCount = (status) => stats?.byStatus?.[status] ?? 0;

  return (
    <PageShell size="full">
      <PageHeader
        icon={HomeRepairServiceOutlinedIcon}
        tone="indigo"
        eyebrow="Windows & Doors"
        title="Service Requests"
        description="Window and door service requests from the sales team. Open one to work the checklist and message the salesperson."
        actions={
          <>
            {/* Queue / Reports toggle */}
            <Segmented
              ariaLabel="Service request views"
              value={activeTab}
              onChange={(key) =>
                updateParams(
                  { view: key === "queue" ? null : key },
                  { push: true }
                )
              }
              options={[
                { key: "queue", label: "Queue" },
                { key: "reports", label: "Reports" },
              ]}
            />
            <ActivityLogButton onOpenRequest={openRequest} />
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon fontSize="small" />}
              disabled={!requests?.length}
              onClick={() =>
                exportRequestsCsv(
                  activeTab === "reports" ? requests || [] : visible,
                  activeTab === "reports"
                    ? "service-requests-all.csv"
                    : "service-requests-filtered.csv"
                )
              }
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => setNewRequestOpen(true)}
            >
              New Request
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" className="!mb-4">
          {error}
        </Alert>
      )}

      <Dialog
        open={newRequestOpen}
        onClose={() => setNewRequestOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle className="flex items-center justify-between gap-3 !pr-3">
          Manual Service Request Entry
          <IconButton
            onClick={() => setNewRequestOpen(false)}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className="!p-0">
          {/* Remount per open so each entry starts from a clean draft. */}
          {newRequestOpen && (
            <WindowServiceRequestForm
              manual
              embedded
              onCreated={(result) => {
                setNewRequestOpen(false);
                navigate(`/windowdoor/service-requests/${result.id}`);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {activeTab === "reports" ? (
        <ServiceRequestReports requests={requests || []} />
      ) : (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_18rem]">
          {/* Table */}
          <div className="min-w-0 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <TextField
                select
                size="small"
                label="Status"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="open">Needs attention (open)</MenuItem>
                <MenuItem value="all">All statuses</MenuItem>
                {STATUS_RAIL.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Assignee"
                value={assigneeFilter}
                onChange={(event) => setAssigneeFilter(event.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="all">Anyone</MenuItem>
                <MenuItem value="unassigned">Unassigned</MenuItem>
                {assigneeOptions.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <DataTable
              rows={visible}
              allRows={requests || []}
              loading={requests === null}
              onRowClick={(row) => openRequest(row._id)}
              initialSort={DEFAULT_SORT}
              initialState={tableStateFromParams(searchParams)}
              onStateChange={onTableChange}
              searchPlaceholder="Search customer, address, salesperson…"
              emptyTitle="No service requests"
              emptyBody="Nothing matches these filters."
              columns={[
                {
                  key: "entry",
                  title: "Entry",
                  sortValue: (row) => Number(row.entry_number) || 0,
                  searchValue: (row) => row.entry_number,
                  render: (row) => (
                    <div className="flex flex-col">
                      <span className="app-accent font-semibold tabular-nums">
                        {row.entry_number}
                      </span>
                      <span className="app-muted whitespace-nowrap text-xs">
                        {shortDate(row.entry_date)}
                      </span>
                    </div>
                  ),
                },
                {
                  key: "customer",
                  title: "Customer",
                  sortValue: (row) => customerLabel(row),
                  searchValue: (row) =>
                    `${customerLabel(row)} ${row.manufacturer || ""} ${
                      row.customer_phone || ""
                    }`,
                  render: (row) => (
                    <div className="flex flex-col">
                      <span className="font-medium">{customerLabel(row)}</span>
                      <span className="app-muted text-xs">
                        {[row.customer_phone, row.manufacturer]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </span>
                    </div>
                  ),
                },
                {
                  key: "address",
                  title: "Address",
                  sortValue: (row) => addressLabel(row),
                  searchValue: (row) => addressLabel(row),
                  render: (row) => (
                    <span className="app-muted block break-words">
                      {addressLabel(row) || "—"}
                    </span>
                  ),
                },
                {
                  key: "contractor",
                  title: "Contractor / Sales",
                  sortValue: (row) => row.contractor || "",
                  searchValue: (row) =>
                    `${row.contractor || ""} ${row.salesperson || ""} ${
                      row.salesperson_email || ""
                    }`,
                  render: (row) => (
                    <div className="flex flex-col">
                      <span className="break-words">
                        {row.contractor || "—"}
                      </span>
                      <span className="app-muted break-words text-xs">
                        {row.salesperson || "—"}
                      </span>
                    </div>
                  ),
                },
                {
                  key: "status",
                  title: "Status",
                  sortValue: (row) => row.status || "",
                  searchValue: (row) => row.status,
                  render: (row) => {
                    const days = daysActive(row);
                    const level = agingLevel(row);
                    return (
                      <div className="flex flex-col items-start gap-1">
                        <StatusChip status={row.status} />
                        {level !== "none" ? (
                          <Tooltip title={AGING_HINT[level]}>
                            <span
                              className={`text-xs font-semibold tabular-nums ${
                                level === "critical"
                                  ? "text-red-600 dark:text-red-400"
                                  : "text-amber-600 dark:text-amber-400"
                              }`}
                            >
                              {days} days open
                            </span>
                          </Tooltip>
                        ) : (
                          isOpen(row.status) && (
                            <span className="app-muted text-xs tabular-nums">
                              {days} day{days === 1 ? "" : "s"} open
                            </span>
                          )
                        )}
                      </div>
                    );
                  },
                },
                {
                  key: "assignee",
                  title: "Assignee",
                  stopClick: true,
                  sortValue: (row) => row.assigned_to?.name || "",
                  searchValue: (row) => row.assigned_to?.name,
                  render: (row) => (
                    <TextField
                      select
                      size="small"
                      value={
                        row.assigned_to?.userID ||
                        (row.assigned_to?.name
                          ? `name:${row.assigned_to.name}`
                          : "")
                      }
                      disabled={savingId === row._id}
                      onChange={(event) => assign(row, event.target.value)}
                      SelectProps={{ displayEmpty: true }}
                      inputProps={{ "aria-label": "Assignee" }}
                      sx={{ minWidth: 150 }}
                    >
                      <MenuItem value="">
                        <span className="app-muted">Unassigned</span>
                      </MenuItem>
                      {/* Imported, name-only assignee: keep them visible in
                          the dropdown; picking a real account replaces it. */}
                      {row.assigned_to?.name && !row.assigned_to?.userID && (
                        <MenuItem value={`name:${row.assigned_to.name}`}>
                          {row.assigned_to.name}
                        </MenuItem>
                      )}
                      {technicians.map((technician) => (
                        <MenuItem
                          key={technician.userID}
                          value={technician.userID}
                        >
                          {technician.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  ),
                },
              ]}
            />
          </div>

          {/* Status rail + workload */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-1 xl:content-start">
            {STATUS_RAIL.map((status) => {
              const style = statusStyle(status);
              const active = statusFilter === status;
              return (
                <button
                  key={status}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setStatusFilter(active ? "open" : status)}
                  className={`app-card flex min-h-[48px] w-full items-center justify-between gap-3 rounded-2xl border px-4 py-2.5 text-left transition hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
                    active ? "ring-2 ring-sky-500" : ""
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      aria-hidden="true"
                      className={`h-2.5 w-2.5 shrink-0 rounded-full ${style.dot}`}
                    />
                    <span className="truncate text-xs font-semibold">
                      {status}
                    </span>
                  </span>
                  <span className="text-lg font-bold tabular-nums">
                    {railCount(status)}
                  </span>
                </button>
              );
            })}

            <div className="app-card col-span-2 rounded-2xl border p-4 sm:col-span-4 xl:col-span-1">
              <h3 className="mb-3 font-semibold">Technician Open Jobs</h3>
              {stats?.workload?.length ? (
                <ul className="space-y-1">
                  {stats.workload.map((row) => {
                    const rowKey = String(row.userID ?? row.name);
                    const selected = String(assigneeFilter) === rowKey;
                    return (
                      <li key={row.userID ?? row.name}>
                        <button
                          type="button"
                          aria-pressed={selected}
                          onClick={() =>
                            setAssigneeFilter(
                              String(assigneeFilter) ===
                                String(row.userID ?? row.name)
                                ? "all"
                                : String(row.userID ?? row.name)
                            )
                          }
                          className={`flex min-h-[36px] w-full items-center justify-between gap-3 rounded-lg px-2.5 py-1.5 text-sm transition hover:bg-[color:var(--app-hover)] ${
                            selected ? "bg-sky-500/10 font-semibold" : ""
                          }`}
                        >
                          <span className="truncate">{row.name}</span>
                          <span className="font-bold tabular-nums">
                            {row.count}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="app-muted text-sm">
                  Nothing assigned right now.
                </p>
              )}
              {typeof stats?.unassigned === "number" && (
                <button
                  type="button"
                  aria-pressed={assigneeFilter === "unassigned"}
                  onClick={() =>
                    setAssigneeFilter(
                      assigneeFilter === "unassigned" ? "all" : "unassigned"
                    )
                  }
                  className={`mt-3 flex min-h-[40px] w-full items-center justify-between gap-3 rounded-lg border-t app-divider px-2.5 pt-3 text-sm transition hover:bg-[color:var(--app-hover)] ${
                    assigneeFilter === "unassigned" ? "bg-sky-500/10" : ""
                  }`}
                >
                  <span className="font-semibold">Unassigned &amp; open</span>
                  <span
                    className={`font-bold tabular-nums ${
                      stats.unassigned > 0
                        ? "text-red-600 dark:text-red-400"
                        : ""
                    }`}
                  >
                    {stats.unassigned}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default ServiceRequestsDashboard;
